// src/components/Account.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import Loading from './Loading';
import DeleteConfirmation from './DeleteConfirmation';
import AccessDeniedModal from './AccessDeniedModal';
import { useLanguage } from '../LanguageContext';

const apiBase = `${API_BASE}/users`;

const UserIcon = ({ name }) => {
  const initials = name ? name.split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase() : 'U';
  const colors = [
    { bg: '#bbdefb', fg: '#1976d2' },
    { bg: '#e0f0ff', fg: '#2c6ecb' },
    { bg: '#ffecd5', fg: '#b86e00' },
    { bg: '#fce4ec', fg: '#bf0711' },
    { bg: '#ede7f6', fg: '#6c3bbf' },
  ];
  const idx = name ? name.charCodeAt(0) % colors.length : 0;
  return (
    <div style={{
      width: 40, height: 40, borderRadius: 10, background: colors[idx].bg, color: colors[idx].fg,
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14,
      flexShrink: 0
    }}>{initials}</div>
  );
};

const Account = ({ user: currentUser }) => {
  const { t, isRtl, language, setLanguage } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  const [showUnauthorized, setShowUnauthorized] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [form, setForm] = useState({ name: '', email: '', password: '', type: 'Member' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) setMenuOpenId(null);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  const fetchUsers = async () => {
    if (!currentUser) return;
    setLoading(true);
    try {
      // Use effective type/role from currentUser
      const isAdmin = currentUser.type === 'Admin' || currentUser.role === 'Admin';

      if (isAdmin) {
        const res = await axios.get(apiBase, { headers: getAuthHeaders() });
        setUsers(res.data || []);
      } else {
        // Members see only themselves
        setUsers([currentUser]);
      }
    } catch (err) {
      console.error('Fetch users error:', err);
      // Don't alert on first load if it might be a temporary auth sync issue
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser) {
      fetchUsers();
    }
  }, [currentUser?.email, currentUser?.type, currentUser?.role]);

  const openAdd = () => {
    const isAdmin = currentUser?.type === 'Admin' || currentUser?.role === 'Admin';
    if (!isAdmin) {
      setShowUnauthorized(true);
      return;
    }
    setIsEditing(false);
    setEditingId(null);
    setForm({ name: '', email: '', password: '', type: 'Member' });
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setIsEditing(true);
    setEditingId(u._id);
    // Find workspace specific role if possible, else fallback to u.type
    // u.workspaces might be populated
    setForm({ name: u.name || '', email: u.email || '', password: '', type: u.type || 'Member' });
    setModalOpen(true);
  };

  const handleDelete = (u) => {
    const isAdmin = currentUser?.type === 'Admin' || currentUser?.role === 'Admin';
    if (!isAdmin) {
      setShowUnauthorized(true);
      return;
    }
    if (u._id === currentUser?._id) {
      alert(t.cannot_delete_own_account);
      return;
    }
    setDeleteConfirm({ open: true, id: u._id, name: u.name });
  };

  const executeDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${apiBase}/${deleteConfirm.id}`, { headers: getAuthHeaders() });
      setDeleteConfirm({ open: false, id: null, name: '' });
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.msg || t.delete_failed);
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || (!isEditing && !form.password)) {
      alert(t.fill_required_fields);
      return;
    }
    setSaving(true);
    try {
      const isAdmin = currentUser?.type === 'Admin' || currentUser?.role === 'Admin';

      if (isEditing && editingId) {
        const payload = { name: form.name, email: form.email };
        if (isAdmin) {
          payload.type = form.type;
          payload.role = form.type; // Sync role with type for this workspace
        }
        if (form.password) payload.password = form.password;
        await axios.put(`${apiBase}/${editingId}`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${apiBase}/register`, {
          name: form.name,
          email: form.email,
          password: form.password,
          type: form.type,
          role: form.type // Send both to satisfy current backend expectations
        }, { headers: getAuthHeaders() });
      }
      setModalOpen(false);
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.msg || t.save_failed);
    } finally {
      setSaving(false);
    }
  };

  const handleLanguageChange = async (newLang) => {
    if (newLang === language) {
        setLanguageModalOpen(false);
        return;
    }
    
    setSaving(true);
    try {
      const res = await axios.put(`${API_BASE}/workspace-settings/language`, { language: newLang }, { headers: getAuthHeaders() });
      setLanguage(newLang);
      
      // Update local storage to persist for session
      const currentWorkspace = JSON.parse(localStorage.getItem('currentWorkspace'));
      currentWorkspace.language = newLang;
      localStorage.setItem('currentWorkspace', JSON.stringify(currentWorkspace));
      
      setLanguageModalOpen(false);
    } catch (err) {
      alert(err.response?.data?.msg || t.failed_update_language);
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = (users || [])
    .filter(u => u.email !== 'admin@example.com')
    .filter(u =>
      (u.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(searchQuery.toLowerCase())
    );

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #c9cccf',
    fontSize: 13, color: '#1a1c1d', background: '#fff', outline: 'none', boxSizing: 'border-box',
    transition: 'border-color 0.2s',
    textAlign: isRtl ? 'right' : 'left'
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', paddingBottom: 60 }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 20, flexWrap: 'wrap', gap: 12
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: '#1a1c1d', textAlign: isRtl ? 'right' : 'left' }}>{t.settings}</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#6d7175', textAlign: isRtl ? 'right' : 'left' }}>
            {currentUser?.type === 'Admin' ? t.manage_team_msg : t.view_account_details_msg}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          {currentUser?.type === 'Admin' && (
            <button onClick={() => setLanguageModalOpen(true)} style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf',
              background: '#fff', color: '#1a1c1d', fontWeight: 600, fontSize: 13,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              transition: 'background 0.2s', flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
              {t.language}
            </button>
          )}
          {currentUser?.type === 'Admin' && (
            <button onClick={openAdd} style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#1976d2', color: '#fff', fontWeight: 600, fontSize: 13,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              transition: 'background 0.2s', flexDirection: isRtl ? 'row-reverse' : 'row'
            }}
              onMouseOver={e => e.currentTarget.style.background = '#1565c0'}
              onMouseOut={e => e.currentTarget.style.background = '#1976d2'}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1v12M1 7h12" stroke="#fff" strokeWidth="2" strokeLinecap="round" /></svg>
              {t.add_user}
            </button>
          )}
        </div>
      </div>

      {/* Main card container */}
      <div style={{
        background: '#fff', borderRadius: 12,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
        border: '1px solid #ebebeb', overflow: 'hidden'
      }}>
        {/* Search / Filter bar (only for Admins) */}
        {currentUser?.type === 'Admin' && (
          <div style={{
            padding: '12px 16px', borderBottom: '1px solid #f1f1f1',
            display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row'
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ position: 'absolute', [isRtl ? 'right' : 'left']: 10, top: '50%', transform: 'translateY(-50%)' }}>
                <path d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.45 4.39l3.08 3.08a.75.75 0 11-1.06 1.06l-3.08-3.08A7 7 0 012 9z" fill="#8c9196" />
              </svg>
              <input
                type="text"
                placeholder={t.search_users}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: isRtl ? '7px 32px 7px 12px' : '7px 12px 7px 32px', borderRadius: 8,
                  border: '1px solid #c9cccf', fontSize: 13, outline: 'none',
                  color: '#1a1c1d', boxSizing: 'border-box',
                  textAlign: isRtl ? 'right' : 'left'
                }}
              />
            </div>
          </div>
        )}

        {/* Users list */}
        {loading ? (
          <div style={{ padding: 40 }}><Loading message={t.syncing_accounts} /></div>
        ) : (
          <div className="fade-in" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, color: '#1a1c1d' }}>
              <thead>
                <tr style={{ background: '#f9fafb', height: 40, borderBottom: '1px solid #ebebeb' }}>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.name}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.email}</th>
                  <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '10px 16px', fontWeight: 600 }}>{t.role}</th>
                  <th style={{ width: 60 }} />
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={4} style={{ padding: 48, textAlign: 'center', color: '#6d7175' }}>
                      {t.no_users_found}
                    </td>
                  </tr>
                ) : filteredUsers.map(u => (
                  <tr
                    key={u._id}
                    style={{ borderBottom: '1px solid #f1f1f1', transition: 'background 0.1s' }}
                    onMouseOver={e => e.currentTarget.style.background = '#f9fafb'}
                    onMouseOut={e => e.currentTarget.style.background = '#fff'}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexDirection: isRtl ? 'row-reverse' : 'row' }}>
                        <UserIcon name={u.name} />
                        <span style={{ fontWeight: 600 }}>{u.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#6d7175' }}>{u.email}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        background: u.type === 'Admin' ? '#bbdefb' : '#f6f6f7',
                        color: u.type === 'Admin' ? '#1976d2' : '#6d7175',
                        padding: '4px 10px', borderRadius: 20,
                        fontSize: 11, fontWeight: 600
                      }}>{u.type === 'Admin' ? t.admin : t.member}</span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        className="menu-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            const rect = e.currentTarget.getBoundingClientRect();
                            setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                            setMenuOpenId(menuOpenId === u._id ? null : u._id);
                          }}
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 8 }}
                      >
                        <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Floating Menu */}
      {menuOpenId && (() => {
        const targetUser = users.find(x => x._id === menuOpenId);
        if (!targetUser) return null;
        return (
          <div className="floating-menu" style={{
            position: 'fixed',
            [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 120),
            top: menuPos.y, background: '#fff',
            borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
            border: '1px solid #ebebeb', direction: isRtl ? 'rtl' : 'ltr'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', flexDirection: 'column', padding: 4 }}>
              <button onClick={() => { setMenuOpenId(null); openEdit(targetUser); }} style={{
                padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row'
              }} onMouseOver={e => e.currentTarget.style.background = '#f1f1f1'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4L18.5 2.5z"></path></svg>
                {t.edit}
              </button>
              {currentUser?.type === 'Admin' && targetUser._id !== currentUser._id && (
                <button onClick={() => {
                  setMenuOpenId(null);
                  handleDelete(targetUser);
                }} style={{
                  padding: '8px 12px', textAlign: isRtl ? 'right' : 'left', border: 'none', background: 'transparent',
                  cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4, display: 'flex', alignItems: 'center', gap: 8, flexDirection: isRtl ? 'row-reverse' : 'row'
                }} onMouseOver={e => e.currentTarget.style.background = '#f1f1f1'} onMouseOut={e => e.currentTarget.style.background = 'transparent'}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  {t.delete}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {modalOpen && (
        <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(4px)' }}>
          <div className="modal-fade-in" style={{
            width: 'min(520px, 94%)', background: '#fff', borderRadius: 12, padding: 0,
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 20px', borderBottom: '1px solid #f1f1f1',
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{isEditing ? t.edit_account : t.add_user}</h2>
              <button onClick={() => setModalOpen(false)} style={{
                border: 'none', background: 'transparent', cursor: 'pointer', color: '#6d7175',
                padding: 4, borderRadius: 6
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" fill="currentColor" /></svg>
              </button>
            </div>

            {/* Modal body */}
            <form onSubmit={handleSubmit}>
              <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#1a1c1d', marginBottom: 6, textAlign: isRtl ? 'right' : 'left' }}>{t.name}</label>
                  <input name="name" value={form.name} onChange={handleChange} placeholder={t.full_name} style={inputStyle} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#1a1c1d', marginBottom: 6, textAlign: isRtl ? 'right' : 'left' }}>{t.email}</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="user@company.com" style={inputStyle} required />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#1a1c1d', marginBottom: 6, textAlign: isRtl ? 'right' : 'left' }}>
                    {isEditing ? t.password_edit_help : t.password}
                  </label>
                  <input name="password" type="password" value={form.password} onChange={handleChange} placeholder={isEditing ? '••••••••' : t.enter_password} style={inputStyle} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#1a1c1d', marginBottom: 6, textAlign: isRtl ? 'right' : 'left' }}>{t.role}</label>
                  {currentUser?.type === 'Admin' ? (
                    <select name="type" value={form.type} onChange={handleChange} style={{ ...inputStyle, appearance: 'auto' }}>
                      <option value="Member">{t.member}</option>
                      <option value="Admin">{t.admin}</option>
                    </select>
                  ) : (
                    <input value={form.type} disabled style={{ ...inputStyle, background: '#f6f6f7' }} />
                  )}
                </div>
              </div>

              {/* Modal footer */}
              <div style={{
                padding: '12px 20px', borderTop: '1px solid #f1f1f1',
                display: 'flex', justifyContent: 'flex-end', gap: 8,
                flexDirection: isRtl ? 'row-reverse' : 'row'
              }}>
                <button type="button" onClick={() => setModalOpen(false)} style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf',
                  background: '#fff', color: '#1a1c1d', fontSize: 13, fontWeight: 500, cursor: 'pointer'
                }}>{t.cancel}</button>
                <button type="submit" disabled={saving} style={{
                  padding: '8px 16px', borderRadius: 8, border: 'none',
                  background: '#1976d2', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer',
                  minWidth: 100, display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {saving ? <div className="dots-loading"><span></span><span></span><span></span></div> : (isEditing ? t.save : t.add_user)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Language Selection Modal */}
      {languageModalOpen && (
        <div className="overlay-fade-in" style={{ position: 'fixed', inset: 0, background: 'rgba(26, 28, 29, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300, backdropFilter: 'blur(4px)' }}>
          <div className="modal-fade-in" style={{
            width: 'min(400px, 94%)', background: '#fff', borderRadius: 12, padding: 0,
            boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
          }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 20px', borderBottom: '1px solid #f1f1f1',
              flexDirection: isRtl ? 'row-reverse' : 'row'
            }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#1a1c1d' }}>{t.select_language}</h2>
              <button onClick={() => setLanguageModalOpen(false)} style={{
                border: 'none', background: 'transparent', cursor: 'pointer', color: '#6d7175',
                padding: 4, borderRadius: 6
              }}>
                <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" fill="currentColor" /></svg>
              </button>
            </div>
            
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div 
                    onClick={() => handleLanguageChange('english')}
                    style={{
                        padding: '12px 16px', borderRadius: 8, border: `2px solid ${language === 'english' ? '#1976d2' : '#ebebeb'}`,
                        background: language === 'english' ? '#e3f2fd' : '#fff', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexDirection: isRtl ? 'row-reverse' : 'row'
                    }}
                >
                    <span style={{ fontWeight: 600 }}>{t.english_lang}</span>
                    {language === 'english' && <svg width="20" height="20" viewBox="0 0 20 20" fill="#1976d2"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" /></svg>}
                </div>
                <div 
                    onClick={() => handleLanguageChange('arabic')}
                    style={{
                        padding: '12px 16px', borderRadius: 8, border: `2px solid ${language === 'arabic' ? '#1976d2' : '#ebebeb'}`,
                        background: language === 'arabic' ? '#e3f2fd' : '#fff', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexDirection: isRtl ? 'row-reverse' : 'row'
                    }}
                >
                    <span style={{ fontWeight: 600 }}>{t.arabic_lang}</span>
                    {language === 'arabic' && <svg width="20" height="20" viewBox="0 0 20 20" fill="#1976d2"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" /></svg>}
                </div>
            </div>

            <div style={{ padding: '12px 20px', borderTop: '1px solid #f1f1f1', display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setLanguageModalOpen(false)} style={{
                  padding: '8px 16px', borderRadius: 8, border: '1px solid #c9cccf',
                  background: '#fff', color: '#1a1c1d', fontSize: 13, fontWeight: 500, cursor: 'pointer'
                }}>{t.cancel}</button>
            </div>
          </div>
        </div>
      )}

      {showUnauthorized && <AccessDeniedModal onBlur={() => setShowUnauthorized(false)} />}

      {deleteConfirm.open && (
        <DeleteConfirmation
          itemName={deleteConfirm.name}
          onConfirm={executeDelete}
          onCancel={() => setDeleteConfirm({ open: false, id: null, name: '' })}
          isDeleting={deleting}
        />
      )}
    </div>
  );
};

export default Account;
